import type { ReactNode } from 'react';
import './glyphs.css';

/**
 * Ícones de linha para rótulos, linhas de ajuste e ações (traço arredondado, a
 * mesma família da seta de voltar). Tomam a cor e o tamanho do texto em volta;
 * dentro de `.gl-tile` viram o selo quadrado das linhas de ajuste.
 */
function G({ children }: { children: ReactNode }) {
  return (
    <svg
      className="gl"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Selo quadrado com o ícone dentro (linhas de Ajustes, Perfil, Aparelhos). */
export function GlyphTile({
  children,
  tone = 'brand',
}: {
  children: ReactNode;
  tone?: 'brand' | 'violet' | 'green' | 'blue' | 'gray' | 'red';
}) {
  return (
    <span className={`gl-tile ${tone}`} aria-hidden="true">
      {children}
    </span>
  );
}

export const GlyphLock = () => (
  <G>
    <rect x="5.5" y="10.5" width="13" height="9.5" rx="2.6" />
    <path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
    <path d="M12 14.4v2" />
  </G>
);

export const GlyphUnlock = () => (
  <G>
    <rect x="5.5" y="10.5" width="13" height="9.5" rx="2.6" />
    <path d="M8.5 10.5V8a3.5 3.5 0 0 1 6.7-1.4" />
    <path d="M12 14.4v2" />
  </G>
);

export const GlyphBell = () => (
  <G>
    <path d="M6.5 16.5V11a5.5 5.5 0 0 1 11 0v5.5l1.5 2H5l1.5-2Z" />
    <path d="M10 20.6a2.2 2.2 0 0 0 4 0" />
  </G>
);

export const GlyphSpark = () => (
  <G>
    <path d="m11 4 1.8 4.7 4.7 1.8-4.7 1.8L11 17l-1.8-4.7-4.7-1.8 4.7-1.8L11 4Z" />
    <path d="m18.2 15.4.7 1.7 1.7.7-1.7.7-.7 1.7-.7-1.7-1.7-.7 1.7-.7.7-1.7Z" />
  </G>
);

export const GlyphSliders = () => (
  <G>
    <path d="M4.5 7h7.6M16.9 7h2.6M4.5 12h2.6M11.9 12h7.6M4.5 17h8.6M17.9 17h1.6" />
    <circle cx="14.5" cy="7" r="2.2" />
    <circle cx="9.5" cy="12" r="2.2" />
    <circle cx="15.5" cy="17" r="2.2" />
  </G>
);

export const GlyphLifebuoy = () => (
  <G>
    <circle cx="12" cy="12" r="8.6" />
    <circle cx="12" cy="12" r="3.4" />
    <path d="m5.9 5.9 3.7 3.7M14.4 14.4l3.7 3.7M18.1 5.9l-3.7 3.7M9.6 14.4l-3.7 3.7" />
  </G>
);

export const GlyphShield = () => (
  <G>
    <path d="M12 3.6 18.6 6v5.4c0 4.2-2.6 7.4-6.6 9-4-1.6-6.6-4.8-6.6-9V6L12 3.6Z" />
  </G>
);

export const GlyphShieldCheck = () => (
  <G>
    <path d="M12 3.6 18.6 6v5.4c0 4.2-2.6 7.4-6.6 9-4-1.6-6.6-4.8-6.6-9V6L12 3.6Z" />
    <path d="m9.1 12 2 2 3.8-4" />
  </G>
);

export const GlyphStar = () => (
  <G>
    <path d="m12 4.2 2.3 4.9 5.3.7-3.9 3.7 1 5.3-4.7-2.6-4.7 2.6 1-5.3-3.9-3.7 5.3-.7L12 4.2Z" />
  </G>
);

export const GlyphMoon = () => (
  <G>
    <path d="M19 14.2A7.6 7.6 0 0 1 9.8 5a7.6 7.6 0 1 0 9.2 9.2Z" />
  </G>
);

export const GlyphImage = () => (
  <G>
    <rect x="3.8" y="5" width="16.4" height="14" rx="2.6" />
    <circle cx="9" cy="10" r="1.6" />
    <path d="m5 17.2 4.6-4.4 3.2 3 2.4-2.2 3.8 3.6" />
  </G>
);

export const GlyphPencil = () => (
  <G>
    <path d="m5 19 .8-3.6L15.9 5.3a1.9 1.9 0 0 1 2.7 0l.1.1a1.9 1.9 0 0 1 0 2.7L8.6 18.2 5 19Z" />
    <path d="m14.2 7 2.8 2.8" />
  </G>
);

export const GlyphCheckCircle = () => (
  <G>
    <circle cx="12" cy="12" r="8.6" />
    <path d="m8.4 12.2 2.5 2.5 4.8-5" />
  </G>
);

export const GlyphBan = () => (
  <G>
    <circle cx="12" cy="12" r="8.6" />
    <path d="m6 6 12 12" />
  </G>
);

export const GlyphFlag = () => (
  <G>
    <path d="M6 20.5V4.5" />
    <path d="M6 5.2h10.5l-1.8 3.6 1.8 3.6H6" />
  </G>
);

export const GlyphTrash = () => (
  <G>
    <path d="M5 7h14M9.5 7V5.2h5V7" />
    <path d="m7 7 .8 11.2a1.6 1.6 0 0 0 1.6 1.5h5.2a1.6 1.6 0 0 0 1.6-1.5L17 7" />
    <path d="M10.4 10.6V16M13.6 10.6V16" />
  </G>
);

export const GlyphExit = () => (
  <G>
    <path d="M10 4.5H7.2A2.2 2.2 0 0 0 5 6.7v10.6a2.2 2.2 0 0 0 2.2 2.2H10" />
    <path d="m14.5 8 4 4-4 4M18.5 12h-9" />
  </G>
);

export const GlyphPhone = () => (
  <G>
    <rect x="7" y="3" width="10" height="18" rx="2.4" />
    <path d="M11 17.8h2" />
  </G>
);

export const GlyphLaptop = () => (
  <G>
    <rect x="5" y="5.5" width="14" height="9.5" rx="1.8" />
    <path d="M3 18.5h18" />
  </G>
);

export const GlyphTv = () => (
  <G>
    <rect x="3" y="5" width="18" height="12" rx="2" />
    <path d="M9 20h6M12 17v3" />
  </G>
);

export const GlyphKey = () => (
  <G>
    <circle cx="8.2" cy="15.8" r="3.4" />
    <path d="m10.7 13.3 8.5-8.5M15.8 8.2l2.4 2.4" />
  </G>
);

export const GlyphEye = () => (
  <G>
    <path d="M2.8 12s3.4-6.2 9.2-6.2 9.2 6.2 9.2 6.2-3.4 6.2-9.2 6.2S2.8 12 2.8 12Z" />
    <circle cx="12" cy="12" r="2.8" />
  </G>
);

export const GlyphChat = () => (
  <G>
    <path d="M5 6.8A2.8 2.8 0 0 1 7.8 4h8.4A2.8 2.8 0 0 1 19 6.8v6.4a2.8 2.8 0 0 1-2.8 2.8h-4.3l-3.6 3v-3h-.5A2.8 2.8 0 0 1 5 13.2V6.8Z" />
    <path d="M8.8 8.6h6.4M8.8 11.6h4" />
  </G>
);

export const GlyphChatOff = () => (
  <G>
    <path d="M8.4 4h7.8A2.8 2.8 0 0 1 19 6.8v6.4c0 .6-.2 1.2-.5 1.6M15 16h-3.1l-3.6 3v-3h-.5A2.8 2.8 0 0 1 5 13.2V7" />
    <path d="m4 4 16 16" />
  </G>
);

export const GlyphAt = () => (
  <G>
    <circle cx="12" cy="12" r="3.4" />
    <path d="M15.4 8.6v4.6a2.3 2.3 0 0 0 4.6 0V12a8 8 0 1 0-3.3 6.5" />
  </G>
);

export const GlyphNote = () => (
  <G>
    <path d="M7 3.8h7.6l3.4 3.4v11.4a1.6 1.6 0 0 1-1.6 1.6H7a1.6 1.6 0 0 1-1.6-1.6V5.4A1.6 1.6 0 0 1 7 3.8Z" />
    <path d="M14.4 4v3.4h3.4M8.6 12h6.8M8.6 15.4h4.4" />
  </G>
);

export const GlyphSmile = () => (
  <G>
    <circle cx="12" cy="12" r="8.6" />
    <path d="M8.4 13.6c.8 1.5 2 2.2 3.6 2.2s2.8-.7 3.6-2.2" />
    <path d="M9.2 9.9h.01M14.8 9.9h.01" strokeWidth="2.6" />
  </G>
);

export const GlyphCalendar = () => (
  <G>
    <rect x="4" y="5.5" width="16" height="14.5" rx="2.6" />
    <path d="M4 10h16M8.5 3.8V7M15.5 3.8V7" />
  </G>
);

export const GlyphUsers = () => (
  <G>
    <circle cx="9" cy="9" r="3" />
    <path d="M3.4 18.6c.6-3 2.7-4.6 5.6-4.6s5 1.6 5.6 4.6" />
    <circle cx="16.6" cy="9.6" r="2.4" />
    <path d="M16.4 14.2c2.2.2 3.7 1.6 4.2 4" />
  </G>
);

export const GlyphVideo = () => (
  <G>
    <rect x="3.6" y="6.4" width="11.8" height="11.2" rx="2.4" />
    <path d="m15.4 10.6 5-2.8v8.4l-5-2.8" />
  </G>
);

export const GlyphMusic = () => (
  <G>
    <path d="M9.5 17.5V6.2l9-1.8v11.2" />
    <circle cx="7.3" cy="17.5" r="2.2" />
    <circle cx="16.3" cy="15.6" r="2.2" />
  </G>
);

export const GlyphMic = () => (
  <G>
    <rect x="9.2" y="3.6" width="5.6" height="10" rx="2.8" />
    <path d="M6 11.4a6 6 0 0 0 12 0M12 17.4v3" />
  </G>
);

export const GlyphClip = () => (
  <G>
    <path d="M16.6 7.4v8.2a4.6 4.6 0 0 1-9.2 0V6.4a3.1 3.1 0 0 1 6.2 0v8.8a1.6 1.6 0 0 1-3.2 0V7.6" />
  </G>
);

export const GlyphPause = () => (
  <G>
    <path d="M9 6v12M15 6v12" />
  </G>
);

export const GlyphPlay = () => (
  <svg className="gl" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M8.2 5.6a1 1 0 0 1 1.5-.9l9.3 6.4a1 1 0 0 1 0 1.7l-9.3 6.4a1 1 0 0 1-1.5-.9V5.6Z" />
  </svg>
);

export const GlyphCall = () => (
  <G>
    <path d="M6.5 3.5h2.6l1.4 4-2 1.3a11 11 0 0 0 6.7 6.7l1.3-2 4 1.4v2.6A2 2 0 0 1 18.3 20 15.5 15.5 0 0 1 4 5.7a2 2 0 0 1 2.5-2.2Z" />
  </G>
);

export const GlyphRoute = () => (
  <G>
    <path d="M12 3.5 19.5 20 12 16.2 4.5 20Z" />
  </G>
);

export const GlyphBookmark = () => (
  <G>
    <path d="M7 4h10v16l-5-3.6L7 20Z" />
  </G>
);

export const GlyphBookmarkOn = () => (
  <G>
    <path d="M7 4h10v16l-5-3.6L7 20Z" fill="currentColor" />
  </G>
);

export const GlyphGlobe = () => (
  <G>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M3.5 12h17M12 3.5c2.4 2.4 3.6 5.2 3.6 8.5s-1.2 6.1-3.6 8.5c-2.4-2.4-3.6-5.2-3.6-8.5s1.2-6.1 3.6-8.5Z" />
  </G>
);

export const GlyphPin = () => (
  <G>
    <path d="M12 21s-6.5-6.2-6.5-11.2a6.5 6.5 0 0 1 13 0C18.5 14.8 12 21 12 21Z" />
    <circle cx="12" cy="9.8" r="2.3" />
  </G>
);

export const GlyphBag = () => (
  <G>
    <path d="M5 8h14l-1 12H6Z" />
    <path d="M9 8V6.5a3 3 0 0 1 6 0V8" />
  </G>
);
